import { brandConfig } from '../brands';

/**
 * Centralized feature flag utility for checking if features/modules are enabled.
 * Components should use this utility instead of directly accessing brand configs.
 *
 * Usage:
 *   import { isFeatureEnabled, getEnabledFeatures } from '@/utils/featureFlags';
 *
 *   // Check a single feature
 *   if (isFeatureEnabled('inquiries')) { ... }
 *
 *   // Get all enabled features
 *   const enabledFeatures = getEnabledFeatures();
 */

/**
 * Check if a specific feature/module is enabled for the current brand.
 *
 * @param {string} featureName - The name of the feature to check (e.g., 'inquiries', 'jobs', 'workOrders')
 * @returns {boolean} - True if the feature is enabled, false otherwise
 *
 * @example
 * // Check if inquiries feature is enabled
 * const canShowInquiries = isFeatureEnabled('inquiries');
 *
 * @example
 * // Use in component for conditional rendering
 * {isFeatureEnabled('inquiries') && <InquiriesLink />}
 */
export const isFeatureEnabled = (featureName) => {
  if (!brandConfig?.features) {
    console.warn('Brand config features not found, defaulting to false');
    return false;
  }

  const isEnabled = brandConfig.features[featureName];

  // Return false for undefined features (explicit opt-in)
  return isEnabled === true;
};

/**
 * Check if multiple features are enabled.
 *
 * @param {string[]} featureNames - Array of feature names to check
 * @returns {boolean} - True if ALL features are enabled, false otherwise
 *
 * @example
 * const canShowJobActions = areFeaturesEnabled(['jobs', 'workOrders']);
 */
export const areFeaturesEnabled = (featureNames) => {
  return featureNames.every((name) => isFeatureEnabled(name));
};

/**
 * Check if at least one of the specified features is enabled.
 *
 * @param {string[]} featureNames - Array of feature names to check
 * @returns {boolean} - True if at least ONE feature is enabled, false otherwise
 *
 * @example
 * const showAnyActions = isAnyFeatureEnabled(['inquiries', 'jobs']);
 */
export const isAnyFeatureEnabled = (featureNames) => {
  return featureNames.some((name) => isFeatureEnabled(name));
};

/**
 * Get an object with all features and their enabled status.
 *
 * @returns {Object} - Object with feature names as keys and boolean enabled status as values
 *
 * @example
 * const features = getEnabledFeatures();
 * // { inquiries: true, jobs: true, ... }
 */
export const getEnabledFeatures = () => {
  return { ...brandConfig?.features };
};

/**
 * Get the current brand name.
 *
 * @returns {string} - The current brand name
 */
export const getCurrentBrand = () => {
  return brandConfig?.brand || 'UNKNOWN';
};

/**
 * Feature names enum for type safety and discoverability.
 * Use these constants instead of string literals.
 */
export const FEATURES = {
  INQUIRIES: 'inquiries',
  JOBS: 'jobs',
  WORK_ORDERS: 'workOrders',
  CLIENTS: 'clients',
  VENDORS: 'vendors',
  INSPECTORS: 'inspectors',
  AGENCIES: 'agencies',
  DATA_MANAGEMENT: 'dataManagement',
  USER_MANAGEMENT: 'userManagement',
  STANDARDS_MASTER: 'standardsMaster',
  CLIENT_QMS_CERTIFICATES: 'clientQmsCertificates',
  FRAMEWORK_AGREEMENTS: 'frameworkAgreements',
};

export default {
  isFeatureEnabled,
  areFeaturesEnabled,
  isAnyFeatureEnabled,
  getEnabledFeatures,
  getCurrentBrand,
  FEATURES,
};
